import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from "react-icons/fa";
import AppContext from '../contexts/AppContext';
import TsLeafNode from '../components/services/TsLeafNode';
import TsNonLeafNode from '../components/services/TsNonLeafNode.js';
import InformModal from '../components/utils/InformModal';
import ConfirmModal from '../components/utils/ConfirmModal';
import FloatingFooter from '../components/utils/FloatingFooter';

function cleanQuillEmpty(html) {
  if (!html) return '';
  const text = html.replace(/<(.|\n)*?>/g, '').trim();
  return text.length === 0 ? '' : html;
}

function normalizeMapUrl(mapUrl) {
  if (Array.isArray(mapUrl)) return mapUrl[0] || '';
  return mapUrl || '';
}

function isNearbyRootId(contentId) {
  return /_c2$/i.test(String(contentId || ''));
}

function isNearbySectionName(sectionName) {
  return /^(nearby|maps?)$/i.test(String(sectionName || '').trim());
}

function isNearbyEntry(detail) {
  const parentId = detail?.content_parent_id || detail?.parentId;
  return !!detail?.isLeaf
    && (
      isNearbyRootId(parentId)
      || (
        !!parentId
        && parentId === detail?.rootNodeId
        && (detail?.sectionType === 'nearby' || isNearbySectionName(detail?.sectionName))
      )
    );
}

function normalizeTsDetail(node) {
  if (!node) return node;
  return {
    ...node,
    nearbyHadCoordinates: [node.mapData?.geoLocation?.lat, node.mapData?.geoLocation?.lng]
      .some(value => value != null && String(value).trim() !== ''),
    mapUrl: normalizeMapUrl(node.mapUrl || (node.sectionType === 'nearby' ? node.mapData?.imageUrl : '')),
    qrImage: node.qrImage || '',
    iconImage: node.iconImage || '',
    content_parent_id: node.content_parent_id || node.parentId || '',
    shuffleChildren: !!node.shuffleChildren,
    pinToTop: !!node.pinToTop,
    mapData: node.mapData
      ? {
          ...node.mapData,
          geoLocation: {
            lat: node.mapData?.geoLocation?.lat ?? '',
            lng: node.mapData?.geoLocation?.lng ?? '',
            address: node.mapData?.geoLocation?.address || '',
          },
        }
      : undefined,
  };
}

const TsDetail = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, uploadImage, userInfo, selectedRole } = useContext(AppContext);
  const [detail, setDetail] = useState(null);
  const isNew = location.pathname.includes('/add') ? (location.pathname.includes('/add/list') ? "list" : "entry") : "old";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isCheckModalOpen, setIsCheckModalOpen] = useState(false);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  const [saveMode, setSaveMode] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (state?.content) {
      setDetail(normalizeTsDetail(state.content));
      return;
    }

    if (isNew === 'old') {
      // 没有从树浏览器带过来的 state.content（比如直接打开 /ts/detail/:id 的链接，
      // 或 ts_content 授权账号被跳转过来）——这类会话没有整棵场馆树可以本地查找，
      // 直接按 id 拉取这一个节点的子树。
      if (!id) return;
      let ignore = false;
      (async () => {
        try {
          if (selectedRole?.id === 'ts_tpa_papua_new_guinea') {
            const treeRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ts/${selectedRole.id}/content/all-nodes/list`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!treeRes.ok) throw new Error(`Failed to load venue content (${treeRes.status})`);
            const findNode = (nodes) => {
              for (const node of nodes || []) {
                if (node.id === id) return node;
                const found = findNode(node.attributes);
                if (found) return found;
              }
              return null;
            };
            const node = findNode(await treeRes.json());
            if (!node) throw new Error('Content is not available in this venue CMS');
            if (!ignore) setDetail(normalizeTsDetail({ ...node, editingScope: 'venue', tsVenueId: selectedRole.id }));
            return;
          }
          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ts/content/${id}/tree`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error(`Failed to load content (${res.status})`);
          const node = await res.json();
          if (!ignore) setDetail(normalizeTsDetail(node));
        } catch (err) {
          console.error('[TsDetail] Failed to load content by id:', err);
        }
      })();
      return () => {
        ignore = true;
      };
    } else {
      const isNearbyParent = isNearbyRootId(id)
        || (id === state?.content?.rootNodeId && isNearbySectionName(state?.content?.sectionName));
      if (isNew === 'entry') {
        setDetail({
          name: '',
          description: '',
          isLeaf: true,
          layoutStyle: 'none',
          content_parent_id: id,
          leftDescription: '',
          rightDescription: '',
          imageUrls: [],
          mapUrl: '',
          qrImage: '',
          searchTags: '',
          shuffleChildren: false,
          pinToTop: false,
          interestTags: [],
          visitDurationMinutes: null,
          eventCategories: [],
          editingScope: state?.content?.editingScope || 'venue',
          tsVenueId: state?.content?.tsVenueId || selectedRole?.id,
          mapData: isNearbyParent
            ? {
                geoLocation: { lat: '', lng: '', address: '' },
              }
            : undefined,
        });
      } else {
        setDetail({
          name: '',
          description: '',
          isLeaf: false,
          layoutStyle: 'overlay',
          content_parent_id: id,
          shuffleChildren: false,
          pinToTop: false,
          editingScope: state?.content?.editingScope || 'venue',
          tsVenueId: state?.content?.tsVenueId || selectedRole?.id,
        });
      }
    }
  }, [id, token, isNew, selectedRole?.id, state?.content]);

  useEffect(() => {
    setHasUnsavedChanges(false);
  }, [detail?.id, id, isNew]);

  useEffect(() => {
    if (!isModalOpen || saveMode === "exit") return undefined;

    const timer = setTimeout(() => {
      setIsModalOpen(false);
      setSaveMode(null);
    }, 1200);

    return () => clearTimeout(timer);
  }, [isModalOpen, saveMode]);

  const isNearbyLocationEntry = isNearbyEntry(detail);
  if (!detail) return <div>Loading...</div>;

  const updateDetail = (valueOrUpdater) => {
    setHasUnsavedChanges(true);
    setDetail((prevDetail) => (
      typeof valueOrUpdater === 'function' ? valueOrUpdater(prevDetail) : valueOrUpdater
    ));
  };

  const handleInputChange = (e, arrayName, index) => {
    setHasUnsavedChanges(true);
    if (arrayName) {
      const value = e ? e.target.value : '';
      setDetail((prevDetail) => ({
        ...prevDetail,
        [arrayName]: prevDetail[arrayName].map((item, i) => (i === index ? value : item)),
      }));
    } else {
      const { name, value } = e.target;
      setDetail((prevDetail) => ({
        ...prevDetail,
        [name]: value,
      }));
    }
  };

  const handleImageChange = async (e, width, height) => {
    const file = e.target.files[0];
    const imagePublicAddress = await uploadImage(file, width, height);
    const name = e.target.name;
    setHasUnsavedChanges(true);
    setDetail((prevDetail) => ({
      ...prevDetail,
      [name]: imagePublicAddress,
    }));
  };

  const handleImageDelete = (name) => {
    setHasUnsavedChanges(true);
    setDetail((prevDetail) => ({
      ...prevDetail,
      [name]: '',
      ...(name === 'mapUrl' && prevDetail.sectionType === 'nearby'
        ? { mapData: { ...prevDetail.mapData, imageUrl: '' } }
        : {}),
    }));
  };

  const saveContent = async () => {
    const isLeafNode = !!detail.isLeaf;
    try {
      const cleanedDetail = {
        ...detail,
        leftDescription: cleanQuillEmpty(detail.leftDescription),
        rightDescription: cleanQuillEmpty(detail.rightDescription),
      };

      const leftEmpty = cleanedDetail.leftDescription === '';
      const rightEmpty = cleanedDetail.rightDescription === '';
      const desc = (cleanedDetail.description || '').trim();

      if (isNearbyLocationEntry && !desc) {
        setIsErrorModalOpen(true);
        setErrorMessage('Short Description is required for Nearby entries.');
        return;
      }
      if (!isNearbyLocationEntry && isLeafNode && leftEmpty && rightEmpty && !desc) {
        setIsErrorModalOpen(true);
        setErrorMessage('At least one of the descriptions must be filled out.');
        return;
      }

      const overLimit = [
        { field: 'description', value: desc },
        {
          field: 'leftDescription',
          value: (cleanedDetail.leftDescription || '').replace(/<(.|\n)*?>/g, '').trim()
        },
        {
          field: 'rightDescription',
          value: (cleanedDetail.rightDescription || '').replace(/<(.|\n)*?>/g, '').trim()
        },
      ].find(f => f.value.length > 2048);
      if (overLimit) {
        setIsErrorModalOpen(true);
        setErrorMessage(`${overLimit.field} exceeds 2048 characters.`);
        return;
      }

      let normalizedMapData = cleanedDetail.mapData;
      if (isNearbyLocationEntry) {
        const isVenueNearby = cleanedDetail.sectionType === 'nearby';
        const rawLatitude = cleanedDetail.mapData?.geoLocation?.lat;
        const rawLongitude = cleanedDetail.mapData?.geoLocation?.lng;
        const latitude = Number(rawLatitude);
        const longitude = Number(rawLongitude);
        const blank = value => value == null || String(value).trim() === '';
        const preserveMissingCoordinates = isVenueNearby && isNew === 'old'
          && cleanedDetail.nearbyHadCoordinates === false
          && blank(rawLatitude) && blank(rawLongitude);
        const invalidCoordinates = isVenueNearby ? (rawLatitude == null || rawLongitude == null
          || String(rawLatitude).trim() === '' || String(rawLongitude).trim() === ''
          || !Number.isFinite(latitude) || !Number.isFinite(longitude)
          || Math.abs(latitude) > 90 || Math.abs(longitude) > 180)
          : (!Number.isFinite(latitude) || !Number.isFinite(longitude));
        if (invalidCoordinates && !preserveMissingCoordinates) {
          setIsErrorModalOpen(true);
          setErrorMessage(isVenueNearby
            ? 'Enter latitude between -90 and 90 and longitude between -180 and 180. Both are required.'
            : 'Nearby entries require valid latitude and longitude.');
          return;
        }

        cleanedDetail.bannerImage = '';
        cleanedDetail.imageUrls = [];
        cleanedDetail.leftDescription = '';
        cleanedDetail.rightDescription = '';

        normalizedMapData = {
          ...(cleanedDetail.mapData || {}),
          category: cleanedDetail.mapData?.category || 'nearby',
          desc,
          imageUrl: cleanedDetail.sectionType === 'nearby'
            ? (cleanedDetail.mapUrl || '')
            : (cleanedDetail.mapUrl || cleanedDetail.mapData?.imageUrl || ''),
          geoLocation: {
            ...(cleanedDetail.mapData?.geoLocation || {}),
            lat: latitude,
            lng: longitude,
            ...(cleanedDetail.mapData?.geoLocation?.address
              ? { address: cleanedDetail.mapData.geoLocation.address.trim() }
              : {}),
          },
        };
        if (preserveMissingCoordinates) {
          // Keep an unconfigured legacy location unconfigured; never invent 0,0.
          normalizedMapData.geoLocation = cleanedDetail.mapData?.geoLocation?.address
            ? { address: cleanedDetail.mapData.geoLocation.address.trim() }
            : undefined;
        }
      }

      const apiUrl = isNew !== "old"
        ? `${process.env.REACT_APP_API_BASE_URL}/ts/content/add`
        : `${process.env.REACT_APP_API_BASE_URL}/ts/content/edit`;
      const requestBody = isNew !== "old"
        ? {
          email: userInfo.user.email,
          ts_venue_id: cleanedDetail.tsVenueId || selectedRole?.id,
          editing_scope: cleanedDetail.editingScope || 'venue',
          content_parent_id: cleanedDetail.content_parent_id,
          isLeaf: cleanedDetail.isLeaf,
          layoutStyle: cleanedDetail.layoutStyle,
          name: (cleanedDetail.name || '').replace(/\s*\(shared\)\s*/ig, '').trim(),
          description: cleanedDetail.description,
          bannerImage: cleanedDetail.bannerImage,
          qrImage: cleanedDetail.qrImage || '',
          iconImage: cleanedDetail.iconImage || '',
          imageUrls: cleanedDetail.imageUrls,
          leftDescription: cleanedDetail.leftDescription,
          rightDescription: cleanedDetail.rightDescription,
          mapUrl: cleanedDetail.mapUrl,
          searchTags: cleanedDetail.searchTags || '',
          mapData: normalizedMapData,
          shuffleChildren: !!cleanedDetail.shuffleChildren,
          pinToTop: !!cleanedDetail.pinToTop,
          interestTags: cleanedDetail.interestTags,
          visitDurationMinutes: cleanedDetail.visitDurationMinutes,
          eventCategories: cleanedDetail.eventCategories
        }
        : {
          email: userInfo.user.email,
          ts_venue_id: cleanedDetail.tsVenueId || selectedRole?.id,
          editing_scope: cleanedDetail.editingScope || 'venue',
          content_id: cleanedDetail.id,
          isLeaf: cleanedDetail.isLeaf,
          layoutStyle: cleanedDetail.layoutStyle,
          name: (cleanedDetail.name || '').replace(/\s*\(shared\)\s*/ig, '').trim(),
          description: cleanedDetail.description,
          bannerImage: cleanedDetail.bannerImage,
          qrImage: cleanedDetail.qrImage || '',
          iconImage: cleanedDetail.iconImage || '',
          imageUrls: cleanedDetail.imageUrls,
          leftDescription: cleanedDetail.leftDescription,
          rightDescription: cleanedDetail.rightDescription,
          mapUrl: cleanedDetail.mapUrl,
          searchTags: cleanedDetail.searchTags || '',
          mapData: normalizedMapData,
          shuffleChildren: !!cleanedDetail.shuffleChildren,
          pinToTop: !!cleanedDetail.pinToTop,
          interestTags: cleanedDetail.interestTags,
          visitDurationMinutes: cleanedDetail.visitDurationMinutes,
          eventCategories: cleanedDetail.eventCategories
        };
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });
      if (response.ok) {
        const data = await response.json();
        if (cleanedDetail.sectionType === 'nearby') {
          setDetail(prev => ({ ...prev, nearbyHadCoordinates:
            normalizedMapData?.geoLocation?.lat != null && normalizedMapData?.geoLocation?.lng != null }));
        }
        setHasUnsavedChanges(false);
        if (saveMode === "exit") {
          setSaveMode(null);
          navigate(-1);
          return data.fileUrl;
        }
        setIsModalOpen(true);
        return data.fileUrl;
      }

      const errorData = await response.json().catch(() => ({}));
      setIsErrorModalOpen(true);
      throw new Error(errorData.error || 'Failed to save content');
    } catch (error) {
      setIsErrorModalOpen(true);
      setErrorMessage(error.message || 'An error occurred while saving content.');
      console.error('Error saving content:', error);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto rounded-lg pb-20">
      <div className="flex items-center space-x-2 py-4">
        <FaArrowLeft
          className={`h-6 w-6 text-gray-700 cursor-pointer ${selectedRole.type === 'adv' ? 'hidden' : ''}`}
          onClick={() => {
            if (hasUnsavedChanges) {
              setIsLeaveConfirmOpen(true);
              return;
            }
            navigate(-1);
          }}
        />
        <div className="text-2xl text-blue-600 font-semibold hidden md:block">{isNew !== "old" ? 'Add Content' : detail.isLeaf ? 'Directory Entry' : 'Directory List'}</div>
      </div>
      <div className="bg-white p-4 shadow-md rounded-lg">
        {detail.isLeaf ? (
          <TsLeafNode
            detail={detail}
            saveContent={saveContent}
            handleImageChange={handleImageChange}
            handleInputChange={handleInputChange}
            handleImageDelete={handleImageDelete}
            setDetail={updateDetail}
            canSwitch={selectedRole.type !== 'adv'}
            isNearbyLocationEntry={isNearbyLocationEntry}
          />
        ) : (
          <TsNonLeafNode
            detail={detail}
            saveContent={saveContent}
            handleImageChange={handleImageChange}
            handleInputChange={handleInputChange}
            handleImageDelete={handleImageDelete}
            setDetail={updateDetail}
          />
        )}
      </div>
      <FloatingFooter hasChanges={hasUnsavedChanges} multipleSave={true}
        onSaveContinue={() => { setIsCheckModalOpen(true); setSaveMode("continue"); }}
        onSaveExit={() => { setIsCheckModalOpen(true); setSaveMode("exit"); }} />
      <InformModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSaveMode(null); }}
        content={isNew !== "old" ? "Successfully Added!" : "Successfully Edited!"}
        type='inform'
      />
      <InformModal
        isOpen={isErrorModalOpen}
        onClose={() => { setIsErrorModalOpen(false); setErrorMessage(''); }}
        content={errorMessage || "Oops! Something went wrong, check again"}
        type='alert'
      />
      <ConfirmModal
        isOpen={isCheckModalOpen}
        onClose={() => { setIsCheckModalOpen(false) }}
        onConfirm={() => { saveContent(); setIsCheckModalOpen(false); }}
        header="Confirm Save"
        content="Are you sure you want to save the changes?" />
      <ConfirmModal
        isOpen={isLeaveConfirmOpen}
        onClose={() => setIsLeaveConfirmOpen(false)}
        onConfirm={() => navigate(-1)}
        header="Unsaved Changes"
        content="Changes will be lost if you leave this page without saving. Do you want to continue?"
      />
    </div>
  );
};

export default TsDetail;

